import React from 'react';
import { motion } from 'framer-motion';
import { Building2, Users, Shield, TrendingUp, Map, Crown, Globe, Target } from 'lucide-react';
export function GovernmentPage() {
  return <div className="min-h-screen bg-surface-bg" data-id="element-629" data-mp="3qwh94uxewbzg">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-630" data-mp="3lxw9awm2nubj">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-631" data-mp="ngafzicmhx4x">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-632" data-mp="doe516pfucja">
            <span className="w-8 h-px bg-teal" data-id="element-633" data-mp="8ql0ei3s9ql4"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-634" data-mp="166ry6sztxac1">
              Applications
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-635" data-mp="11f0dgz3ctytt"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-636" data-mp="3pommi8u1nmb1">
            Government and Public Sector Applications
          </h1>

          <p className="text-xl text-ink-muted leading-relaxed max-w-3xl mx-auto" data-id="element-637" data-mp="3fykwhbi4rtth">
            SyNeural's platform architecture is designed to help governments and
            public institutions build stronger human capability, more resilient
            communities, better-functioning institutions, and more rigorous
            leadership pipelines.
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-638" data-mp="2x0gztgjfjn8k">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-639" data-mp="31yigxvxapche">
          <p className="text-lg text-ink-muted leading-relaxed mb-6" data-id="element-640" data-mp="xt9z909zn7tl">
            Through CJI and NCJI, the platform provides a structured framework
            for understanding judgment, identifying readiness gaps, connecting
            those gaps to development pathways, and strengthening public systems
            over time.
          </p>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-641" data-mp="juk4vsejzm8x">
            The platform is relevant across multiple levels of government. At
            the local level, it can help identify community bottlenecks such as
            weak youth pathways, low workforce readiness, or heightened
            misinformation vulnerability. At the regional level, it can support
            education strategy, workforce development, resilience planning, and
            leadership cultivation. At the national level, it can support
            civil-service modernization, ministry-level planning, institutional
            capability mapping, and executive-level readiness.
          </p>
        </motion.div>
      </section>

      {/* Government Levels */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface-muted border-y border-line" data-id="element-642" data-mp="27q8eytonb3h1">
        <div className="max-w-5xl mx-auto" data-id="element-643" data-mp="2cq4kwygwl4z8">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16" data-id="element-644" data-mp="2co9xndi5t0rz">
            <div className="bg-white p-8 rounded-lg border border-line text-center" data-id="element-645" data-mp="2vh4ao1w82itk">
              <div className="w-14 h-14 rounded-full bg-teal/10 flex items-center justify-center text-teal mx-auto mb-4" data-id="element-646" data-mp="30ew5p4s5yl1z">
                <Building2 size={28} data-id="element-647" data-mp="35cfkc5srwzug" />
              </div>
              <h3 className="text-xl font-heading font-bold text-ink-dark mb-2" data-id="element-648" data-mp="39h5cpko83wam">
                Municipal & Local
              </h3>
              <p className="text-sm text-ink-muted" data-id="element-649" data-mp="axhuvtrs7xt1">
                Community capability patterns and local development
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg border border-line text-center" data-id="element-650" data-mp="jquv7gs6e1p5">
              <div className="w-14 h-14 rounded-full bg-navy-light/10 flex items-center justify-center text-navy-light mx-auto mb-4" data-id="element-651" data-mp="fpswd1267rnp">
                <Map size={28} data-id="element-652" data-mp="3o6nxoc3bl3zo" />
              </div>
              <h3 className="text-xl font-heading font-bold text-ink-dark mb-2" data-id="element-653" data-mp="3j7b1h0qk6kkf">
                Regional & Provincial
              </h3>
              <p className="text-sm text-ink-muted" data-id="element-654" data-mp="127qizdwvec3">
                Broader planning across education, labor, and resilience
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg border border-line text-center" data-id="element-655" data-mp="1cgbn8dbvvgku">
              <div className="w-14 h-14 rounded-full bg-navy/10 flex items-center justify-center text-navy mx-auto mb-4" data-id="element-656" data-mp="17higd3r3qntn">
                <Globe size={28} data-id="element-657" data-mp="1fxziyj016wyn" />
              </div>
              <h3 className="text-xl font-heading font-bold text-ink-dark mb-2" data-id="element-658" data-mp="1g0lu0sm4rulm">
                National Government
              </h3>
              <p className="text-sm text-ink-muted" data-id="element-659" data-mp="160ux6ka3l3id">
                Ministry-level planning and civil-service modernization
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Application Areas */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto space-y-20" data-id="element-660" data-mp="1ypimcqxywd17">
        {/* Municipal and Local Government */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-661" data-mp="23pwo5r8z6qqs">
          <div className="flex items-center gap-4 mb-6" data-id="element-662" data-mp="2mebaw2c9fils">
            <div className="w-14 h-14 rounded-xl bg-teal flex items-center justify-center text-white shadow-lg" data-id="element-663" data-mp="2mg603sukdwvo">
              <Building2 size={28} data-id="element-664" data-mp="2w8h65xtpsd01" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-665" data-mp="2rb767o8p8ixy">
              Municipal and Local Government
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-666" data-mp="2me8gdhw2iy5k">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-667" data-mp="2icvbba0suyzp">
              At the municipal level, SyNeural can help public leaders and local
              institutions better understand the human capability patterns
              shaping community outcomes. This may include identifying areas
              with weak youth-to-opportunity pipelines, low workforce readiness,
              limited leadership development, or elevated vulnerability to
              rumor, manipulation, and civic instability. This makes the
              platform useful for local education initiatives, youth programs,
              resilience efforts, and community-development planning.
            </p>
          </div>
        </motion.div>

        {/* Regional and Provincial Government */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-668" data-mp="a1m59luu9uf6">
          <div className="flex items-center gap-4 mb-6" data-id="element-669" data-mp="3iktnd6q0fb2m">
            <div className="w-14 h-14 rounded-xl bg-navy-light flex items-center justify-center text-white shadow-lg" data-id="element-670" data-mp="3mjssarbqr7u2">
              <Map size={28} data-id="element-671" data-mp="3rivm1fm9trmq" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-672" data-mp="4zl6zac77e7">
              Regional and Provincial Government
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-673" data-mp="2yugj3rgeyzrr">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-674" data-mp="33srrryuz348e">
              At the regional level, SyNeural can support broader planning
              across education, labor, public health, social development, and
              leadership preparation. The platform can help map capability gaps
              across districts or provinces, identify opportunity bottlenecks,
              and support more targeted approaches to workforce development,
              institutional strengthening, and civic resilience. This makes it
              relevant for regional modernization strategies and long-range
              public planning.
            </p>
          </div>
        </motion.div>

        {/* National Government */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-675" data-mp="2pxihwcaht3o6">
          <div className="flex items-center gap-4 mb-6" data-id="element-676" data-mp="2tv6i9uuyfong">
            <div className="w-14 h-14 rounded-xl bg-navy flex items-center justify-center text-white shadow-lg" data-id="element-677" data-mp="3dk4b4u5zr5wh">
              <Globe size={28} data-id="element-678" data-mp="3ih4ir9ihogpp" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-679" data-mp="34inzkj3fwxqo">
              National Government
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-680" data-mp="38i58jmi2sd0l">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-681" data-mp="2urjmxq18loz0">
              At the national level, SyNeural can function as part of a larger
              capability and readiness architecture. The platform can support
              ministry-level planning, civil-service development, education
              reform, workforce strategy, national resilience analysis, and
              leadership-readiness frameworks. It is designed to help
              governments better understand the human layer beneath
              institutional performance and use that understanding to build
              stronger long-term systems.
            </p>
          </div>
        </motion.div>

        {/* Civil Service Modernization */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-682" data-mp="yiqzmjmvn87p">
          <div className="flex items-center gap-4 mb-6" data-id="element-683" data-mp="1dc3yzrbd0mt1">
            <div className="w-14 h-14 rounded-xl bg-green-soft flex items-center justify-center text-white shadow-lg" data-id="element-684" data-mp="1ha1o6u54r8wf">
              <Shield size={28} data-id="element-685" data-mp="13joh0u02by85" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-686" data-mp="18ihwut2to1bh">
              Civil Service Modernization
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-687" data-mp="1w7aqgivz1x2x">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-688" data-mp="210vlc229qaxw">
              SyNeural can support civil-service modernization by helping public
              institutions identify the capabilities that matter most in
              authority-bearing roles. These include reasoning quality,
              emotional regulation, consequence awareness, systems thinking,
              integrity under pressure, and responsibility at scale. The
              platform can support development pathways, readiness analysis, and
              more structured approaches to public-sector advancement and
              leadership cultivation.
            </p>
          </div>
        </motion.div>

        {/* Public Workforce and Institutional Readiness */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-689" data-mp="1wemm3hz61ssk">
          <div className="flex items-center gap-4 mb-6" data-id="element-690" data-mp="3fuzb28sylrnw">
            <div className="w-14 h-14 rounded-xl bg-teal flex items-center justify-center text-white shadow-lg" data-id="element-691" data-mp="3fwk6e0n3lemt">
              <Users size={28} data-id="element-692" data-mp="3ouw4sou7hgpt" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-693" data-mp="3ow78konniogx">
              Public Workforce and Institutional Readiness
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-694" data-mp="2w6qk2235jrpz">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-695" data-mp="3151sq9hpnw6m">
              Governments rely on people in roles where poor judgment can weaken
              trust, create inefficiency, or generate serious public
              consequences. SyNeural helps create a stronger framework for
              understanding readiness across those environments. It can support
              workforce development, public-sector training, role qualification,
              succession planning, and institutional capability-building by
              identifying where readiness is strong, where it is weak, and where
              support systems need to improve.
            </p>
          </div>
        </motion.div>

        {/* Civic Resilience and Community Stability */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-696" data-mp="20z7tjk8dntg4">
          <div className="flex items-center gap-4 mb-6" data-id="element-697" data-mp="1n3voaiwlikky">
            <div className="w-14 h-14 rounded-xl bg-navy-light flex items-center justify-center text-white shadow-lg" data-id="element-698" data-mp="1i7ezrnlhi47c">
              <Target size={28} data-id="element-699" data-mp="1d9din87mceyi" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-700" data-mp="188z6bdyziiuo">
              Civic Resilience and Community Stability
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-701" data-mp="148yrvtbvy7nm">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-702" data-mp="1sya3tbqzcepe">
              Public institutions increasingly operate in environments shaped by
              misinformation, distrust, emotional volatility, and fragmented
              civic participation. SyNeural can help governments better
              understand the conditions that strengthen or weaken civic
              resilience, including manipulation vulnerability, low
              consequential thinking, weak opportunity alignment, and
              underdeveloped local leadership. This makes the platform relevant
              not just for internal institutions, but for wider community
              stability and public trust.
            </p>
          </div>
        </motion.div>

        {/* Leadership Development and High-Responsibility Roles */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-703" data-mp="2zlymjyh4rynt">
          <div className="flex items-center gap-4 mb-6" data-id="element-704" data-mp="xo4zwdmmv6pe">
            <div className="w-14 h-14 rounded-xl bg-navy flex items-center justify-center text-white shadow-lg" data-id="element-705" data-mp="xppug38y6pn9">
              <Crown size={28} data-id="element-706" data-mp="svvbx352wgpr" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-707" data-mp="17ljdm5h69he0">
              Leadership Development and High-Responsibility Roles
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-708" data-mp="1gn0q7dvdf0xy">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-709" data-mp="1bo68kf953iff">
              As the level of responsibility rises, the cost of poor judgment
              rises with it. SyNeural is designed to become more rigorous as the
              stakes of decision-making increase. In government settings, this
              makes the platform especially relevant for leadership development,
              senior administrative roles, and executive-level readiness. It can
              support more structured approaches to evaluating judgment,
              consequence reasoning, responsibility, and institutional
              reliability in roles where large systems and populations are
              affected.
            </p>
          </div>
        </motion.div>

        {/* Strategic Planning and Capability Mapping */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-710" data-mp="259tv6gg17zy5">
          <div className="flex items-center gap-4 mb-6" data-id="element-711" data-mp="2j2t6e68s77zw">
            <div className="w-14 h-14 rounded-xl bg-green-soft flex items-center justify-center text-white shadow-lg" data-id="element-712" data-mp="2izxasokeovem">
              <TrendingUp size={28} data-id="element-713" data-mp="ce0xl56m6nqb" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-714" data-mp="cesv4ljruvj8">
              Strategic Planning and Capability Mapping
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-715" data-mp="3dunzue1e7wf">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-716" data-mp="3ujw2ye14hlnc">
              At scale, SyNeural can support governments through aggregate
              capability mapping and strategic analysis. This includes
              identifying regional bottlenecks, workforce-readiness patterns,
              leadership-pipeline depth, misinformation vulnerability, and
              broader opportunity constraints. Over time, this can help support
              more coherent long-range planning for education, labor,
              resilience, public-sector development, and social stability.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Why It Matters */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-navy-deep text-white" data-id="element-717" data-mp="idyhhq37jxd8">
        <div className="max-w-4xl mx-auto" data-id="element-718" data-mp="n4ey1j2brz3t">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-719" data-mp="n5gk0ikvq60b">
            <h2 className="text-3xl font-heading font-bold mb-6" data-id="element-720" data-mp="112dauk0zznh7">
              Why It Matters for Government
            </h2>
            <p className="text-xl leading-relaxed opacity-90" data-id="element-721" data-mp="s3rt2r3cochh">
              Governments are often asked to solve problems at the policy level
              that are deeply connected to human capability, institutional
              readiness, and leadership quality. SyNeural offers a more
              structured way to examine those underlying conditions. It helps
              connect human development to public performance, civic resilience
              to institutional trust, and leadership readiness to long-term
              governance stability. That makes it relevant not only as a
              platform for analysis, but as a broader framework for public
              capability and institutional strength.
            </p>
          </motion.div>
        </div>
      </section>
    </div>;
}